/* eslint-disable unicorn/prefer-dom-node-text-content */
import { Cookies } from 'react-cookie';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { loadCss, loadScript } from '../LoadResources';
import { ROUTE_PATHS } from '../routes/routePaths';
import type { RedirectData } from '../types/products';
import type { GridRedirectResponseData, RedirectResponseData } from '../types/redirect';
import { LayoutDirection } from '../types/singleReport';
import { contactUsEmail } from './constants';
import { logger } from './logger';
import { requestParentProductHome } from './parentShell';

export const getAccessToken = (): string | null => {
  return localStorage.getItem('access_token');
};
export const getRefreshToken = (): string | null => {
  return localStorage.getItem('refresh_token');
};

export const getLoginUserId = (): string | null => {
  return localStorage.getItem('loginUserId'); // or sessionStorage or cookies
};

export const getLoginUserOrgId = (): string | null => {
  return localStorage.getItem('loginUserOrgId'); // or sessionStorage or cookies
};
export const getLoginUserOrgType = (): string | null => {
  return localStorage.getItem('loginUserOrgType'); // or sessionStorage or cookies
};
export const getLoginUseRoleId = (): string | null => {
  const userData = JSON.parse(localStorage.getItem('userData') ?? "{}");
  return userData["roleId"] ?? null;
};

export const getLoginUserEmailAddress = (): string | null => {
  try {
    const userData = JSON.parse(localStorage.getItem('userData') ?? '{}') as {
      emailAddress?: string;
    };
    return userData.emailAddress ?? null;
  } catch {
    return null;
  }
};

type MouseNavigationEvent = {
  button: number;
  ctrlKey: boolean;
  metaKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
};

export const isModifiedNavigationClick = (event: MouseNavigationEvent): boolean => {
  return event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey;
};

type HandleProductRedirectOptions = {
  redirectData?: RedirectData;
  productId: number;
  categoryId?: number | string;
  categoryType?: number | string;
  navigate: (path: string) => void;
  setEncryptInput: (value: string) => void;
  onUnknownRedirect?: () => void;
};

export const handleProductRedirectAction = ({
  redirectData,
  productId,
  categoryId,
  categoryType,
  navigate,
  setEncryptInput,
  onUnknownRedirect,
}: HandleProductRedirectOptions): void => {
  if (redirectData?.htmlType === 2 && redirectData?.htmlText) {
    window.open(redirectData.htmlText, '_blank');
    return;
  }

  switch (redirectData?.redirect) {
    case 'PRODUCT_HOME_PAGE': {
      const resolvedProductId =
        redirectData.productId > 0 ? redirectData.productId : productId;
      if (
        resolvedProductId > 0 &&
        requestParentProductHome({
          productId: resolvedProductId,
          canonicalUrl: redirectData.urlInfo?.canonicalUrl,
          catId: redirectData.catId,
          catType: redirectData.catType,
        })
      ) {
        return;
      }
      const query = `productId=${resolvedProductId}&categoryId=${redirectData.catId ?? categoryId ?? ''}&categoryType=${redirectData.catType ?? categoryType ?? ''}`;
      setEncryptInput(encodeURIComponent(query));
      return;
    }
    case 'FIND_REPORTS': {
      safeNavigate(
        navigate,
        `${ROUTE_PATHS.FIND_REPORTS}?categoryId=${categoryId ?? ''}&categoryType=${categoryType ?? ''}&source=${redirectData?.productId ?? ''}`,
      );
      return;
    }
    default: {
      onUnknownRedirect?.();
    }
  }
};

export const navigateToEncryptedProduct = (
  encryptedValue: string,
  navigate: (path: string) => void
): void => {
  safeNavigate(navigate, `${ROUTE_PATHS.PRODUCT}?eURL=${encodeURIComponent(encryptedValue)}`);
};

export const clearAllCookies = () => {
  const cookies = new Cookies();
  const allCookies = cookies.getAll();
  Object.keys(allCookies).forEach((cookieName) => {
    // Preserve CookieControl cookie (should persist for 90 days)
    if (cookieName === 'CookieControl') {
      return;
    }
    cookies.remove(cookieName, { path: '/' });
  });
};

const getOrdinal = (n: number): string => {
  if (n > 3 && n < 21) return `${n}th`;
  switch (n % 10) {
    case 1: {
      return `${n}st`;
    }
    case 2: {
      return `${n}nd`;
    }
    case 3: {
      return `${n}rd`;
    }
    default: {
      return `${n}th`;
    }
  }
};

export const formatDate = (
  dateString?: string,
  format = 'DD MMM YYYY'
): string => {
  if (!dateString) return 'None';
  const date = dayjs(dateString);
  return format === 'Do MMM, YYYY'
    ? `${getOrdinal(date.date())} ${date.format('MMMM')}, ${date.format('YYYY')}`
    : date.format(format);
};

export const handlePrint = (html: string, dynamicStyles: string = '') => {
  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  document.body.append(iframe);

  const iframeDocument =
    iframe.contentDocument || iframe.contentWindow?.document;
  if (!iframeDocument) return;

  iframeDocument.open();
  iframeDocument.write(`
    <html>
      <head>
        <title>aosphere</title>

        <!-- Load CSS from public folder -->
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link href="https://fonts.googleapis.com/css2?family=EB+Garamond:wght@400;500&amp;family=Source+Sans+Pro:wght@300;400;600;700&amp;display=swap" rel="stylesheet">
        <link rel="stylesheet" href="/css/bootstrap.css" />
        <link rel="stylesheet" href="/css/common.css" />
        <style>
       ${dynamicStyles}
        </style>
        </head>
      <body>
        ${html}
        <script>
          window.onload = function() {
            setTimeout(() => {
              window.print();
              parent.document.body.removeChild(parent.document.querySelector('iframe'));
            }, 300);
          };
        </script>
      </body>
    </html>
  `);
  iframeDocument.close();
};

export const truncateText = (text: string, maxLength: number = 85): string => {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};
export const copyToClipboard = (
  elementRef: React.RefObject<HTMLElement | null>,
  onSuccess?: () => void,
  onError?: (error: unknown) => void
): void => {
  const element = elementRef.current;

  if (element) {
    const text = element.innerText;
    navigator.clipboard
      .writeText(text)
      .then(() => {
        onSuccess?.();
      })
      .catch((error) => {
        onError?.(error);
        logger.error('Failed to copy:', error);
      });
  }
};

export const parseLayout = (value?: string): LayoutDirection =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  value === LayoutDirection.Horizontal
    ? LayoutDirection.Horizontal
    : LayoutDirection.Vertical;
export const getAlertPlainTextTitleHover = (source: string): string => {
  if (!source) return '';

  // Parse the HTML safely using DOMParser
  const parser = new DOMParser();
  const document_ = parser.parseFromString(source, 'text/html');
  let result = document_.body.textContent ?? '';

  // Normalize whitespace: collapse multiple spaces
  result = result.replaceAll(/\s{2,}/g, ' ').trim();

  // Replace known HTML entities with readable text
  const entityMap: Record<string, string> = {
    '&nbsp;': ' ',
    '&bull;': ' * ',
    '&lsaquo;': '<',
    '&rsaquo;': '>',
    '&trade;': '(tm)',
    '&frasl;': '/',
    '&lt;': '<',
    '&gt;': '>',
    '&copy;': '(c)',
    '&reg;': '(r)',
    '&#39;': "'",
    '&apos;': "'",
    '&quot;': '"',
    '&ndash;': '-',
    '&mdash;': '-',
    '&iexcl;': '¡',
    '&amp;': '&',
    '&rsquo;': "'",
  };

  for (const [entity, replacement] of Object.entries(entityMap)) {
    result = result.replaceAll(entity, replacement);
  }

  // Remove any remaining HTML entities (e.g., &abcd;)
  result = result.replaceAll(/&.{2,6};/g, '');

  // Optional: Reconvert (r) to &reg; if needed (depends on your business logic)
  result = result.replaceAll('(r)', '&reg;');

  return result;
};

export const constantSingleColumn = 101;
export const constantMultiColumn = 102;
export const constantTab = 103;
export const constantPlain = 104;
export const applicationServiceName = "aosphere LLP 3.1"
export const emailDisclaimer = `
Your use of the aoslogin website (including your data while accessing aoslogin) are governed by the Privacy Policy, Cookie Policy and the terms of use of the website set out in the Legal Notice, all of which are available on the website. Your use of all content made available on the website is governed by the terms and conditions which have been entered into by your organisation, with which you are required to comply. Your login and password details for this service are confidential and must not be disclosed to or used by any other person. Access to this website may be monitored and audited in order to prevent misuse of the content and data made available, which is owned by aosphere. If you do not have an account with aosphere or have received this communication in error please notify us immediately by e-mailing <a href='mailto:${contactUsEmail}'>${contactUsEmail}</a>. The contents of this email do not constitute legal or other professional advice.<br/><br/>
aosphere Limited is registered in England and Wales with registered number 15371365 and registered office The Steward Building, 12 Steward Street, London, E1 6FQ, United Kingdom. aosphere Limited is registered for VAT purposes with VAT registration number GB 462 1046 24.
`;
export function changeDefinitionFunctionString(
  concatOpinion: string,
  responseTxt: string = ' '
): string {
  // Check if opinion is already included (case-insensitive)
  if (!responseTxt?.toLowerCase().includes(concatOpinion?.toLowerCase())) {
    // Build replacement pattern: ','YourOpinion');
    const replaceByPattern = `','${concatOpinion}');`;

    // Replace the ending '); with ','YourOpinion');
    return responseTxt?.replaceAll(/'\);/gi, replaceByPattern);
  }

  // If already included, return original string
  return responseTxt;
}
export function getRedirectPath(response: RedirectResponseData, renderTypeIdOverride?: number): string {
  const pageKey = response.reportPageEnum;
  const effectiveRenderTypeId = renderTypeIdOverride ?? Number((response as RedirectResponseData & { renderTypeId?: number | null }).renderTypeId ?? 0);
  const apiRenderTypeQuery = effectiveRenderTypeId === 105 ? '&renderTypeId=105' : '';
  const allowedPages: Array<keyof typeof ROUTE_PATHS> = [
    'OPINION_COMPARE',
    'REPORT_SINGLE',
    'REPORT_TAB',
    'REPORT_MULTITEMPLATE',
    'REPORT_VIEW_COMPARE',
    'OPINION_ADD',
    'OPINION_DRAFT',
    'OPINION_ARCHIVED',
    'API_PRODUCT'
  ];
  const eurl = response.eurl;

  switch (pageKey) {
    case 'REPORT_SINGLE': {
      return `${ROUTE_PATHS.REPORT_SINGLE}?eurl=${eurl}`;
    }
    case 'REPORT_TAB': {
      return `${ROUTE_PATHS.REPORT_TAB}?eurl=${eurl}`;
    }
    case 'REPORT_MULTITEMPLATE': {
      return `${ROUTE_PATHS.REPORT_MULTITEMPLATE}?eurl=${eurl}`;
    }
    case 'OPINION_COMPARE': {
      return `${ROUTE_PATHS.OPINION_COMPARE}?eurl=${eurl}`;
    }
    case 'REPORT_VIEW_COMPARE': {
      return eurl
        ? `${ROUTE_PATHS.REPORT_VIEW_COMPARE}?eurl=${eurl}`
        : `${ROUTE_PATHS.REPORT_VIEW_COMPARE}`;
    }
    case 'OPINION_ADD': {
      return `${ROUTE_PATHS.OPINION_ADD}?eurl=${eurl}`;
    }
    case 'OPINION_DRAFT': {
      return `${ROUTE_PATHS.OPINION_DRAFT}?eurl=${eurl}`;
    }
    case 'OPINION_FUNCTION_ADVANCE_DISPLAY_SETTINGS': {
      return `${ROUTE_PATHS.OPINION_ADD}?eurl=${eurl}&showPart4Only=true`;
    }
    case 'OPINION_ADD_ONLY_PART1': {
      return `${ROUTE_PATHS.OPINION_ADD}?eurl=${eurl}&showPart1Only=true${apiRenderTypeQuery}`;
    }
    case 'OPINION_SELECT_QUES': {
      return `${ROUTE_PATHS.OPINION_ADD}?eurl=${eurl}&showPart2And3Only=true`;
    }
    case 'OPINION_ARCHIVED': {
      return `${ROUTE_PATHS.OPINION_ARCHIVED}?eurl=${eurl}&showPart1Only=true${apiRenderTypeQuery}`;
    }
    case 'OPINION_SELECTQUES': {
      return `${ROUTE_PATHS.OPINION_ADD}?eurl=${eurl}&showPart2Only=true`;
    }
    case 'AJAX_OPINION_ANSWER_QUESTION': {
      return `${ROUTE_PATHS.OPINION_ADD}?eurl=${eurl}&showPart3Only=true`;
    }
    case 'REPORT': {
      return `${ROUTE_PATHS.REPORT_SINGLE}?eurl=${eurl}`;
    }
    case 'API_PRODUCT': {
      return `${ROUTE_PATHS.API_PRODUCT}?eURL=${eurl}`;
    }
    default: {
      return allowedPages.includes(pageKey) ? ROUTE_PATHS[pageKey] : ROUTE_PATHS.NOT_FOUND;
    }
  }
}
/**
 * Keep listing category + a single productId on grid view URLs.
 * Multi-product rows omit productId so V3 uses the manage-alerts shell.
 */
export function withGridCategoryQuery(
  path: string,
  categoryId?: string | number | null,
  categoryType?: string | number | null,
  productId?: string | number | null
): string {
  if (!path || path === ROUTE_PATHS.NOT_FOUND) {
    return path;
  }

  const parameters = new URLSearchParams();
  const parsedCategoryId = Number(categoryId);
  if (Number.isFinite(parsedCategoryId) && parsedCategoryId > 0) {
    parameters.set('categoryId', String(parsedCategoryId));
    const parsedCategoryType = Number(categoryType);
    if (Number.isFinite(parsedCategoryType) && parsedCategoryType > 0) {
      parameters.set('categoryType', String(parsedCategoryType));
    }
  }

  const parsedProductId = Number(productId);
  if (Number.isInteger(parsedProductId) && parsedProductId > 0) {
    parameters.set('productId', String(parsedProductId));
  }

  if ([...parameters.keys()].length === 0) {
    return path;
  }

  return `${path}${path.includes('?') ? '&' : '?'}${parameters.toString()}`;
}

export function getGridRedirectPath(response: GridRedirectResponseData, keyvalue?: string): string {
  const eurl = response.eurl;
  const { redirectPage } = response;
  switch (redirectPage) {
    case 'GRID_ENABLE_FILTER': {
      return `${ROUTE_PATHS.VIEW_GRID_FILTER}?eurl=${eurl}`;
    }
    case 'GRID_SINGLE': {
       return keyvalue && keyvalue === 'pub' ? `${ROUTE_PATHS.VIEW_GRID}?eurl=${eurl}&isLiveGrid=1` : `${ROUTE_PATHS.VIEW_GRID}?eurl=${eurl}`;
    }
    case 'GRID_PART1': {
      return `${ROUTE_PATHS.EDIT_GRID}?eurl=${eurl}`;
    }
    case 'GRID_SETTING_INDIVIDUAL': {
      return `${ROUTE_PATHS.EDIT_GRID}?eurl=${eurl}`;
    }
    case 'GRID_DRAFT': {
      return `${ROUTE_PATHS.DRAFT_GRIDS}?eurl=${eurl}`;
    }
    case 'GRID_ARCHIVED': {
      return `${ROUTE_PATHS.ARCHIVED_GRIDS}?eurl=${eurl}`;
    }
    default: {
      return ROUTE_PATHS.NOT_FOUND;
    }
  }
}
export const MSG_INTRO = 'Are you sure you want to activate';
export const MSG_APPROVAL =
  'Please ensure that you have internal approval to access this jurisdiction.';
export const MSG_NOTE =
  "Please note this will use one of your organisation's credits. Your organisation's remaining allowance after activation will be";
export const entityModalText: string =
  `Please enter job title and select the entity you are acting for or <a class='linkRed' href='mailto:${contactUsEmail}'>click here</a> if you cannot find the entity in the list.`;
export const ActivateJurisdictionMessage3 = ' jurisdictions.';
export const ActivateJurisdictionSingularMessage3 = ' jurisdiction.';
export const ADVANCE_DISPLAY_CONFIG_SHOW_ALERT = 101;
export function addAttributesToTag(
  htmlString: string,
  tagName: string
): string {
  if (!htmlString || !tagName) return htmlString;

  const parser = new DOMParser();
  const document = parser.parseFromString(htmlString, 'text/html');
  const tags = document.querySelectorAll<HTMLElement>(tagName);

  const usedIds = new Set<string>();

  tags.forEach((tag) => {
    const text = tag.textContent?.trim() || '';

    // Generate slug from text
    const id = text
      .toLowerCase()
      // eslint-disable-next-line unicorn/prefer-string-replace-all
      .replace(/[^\d\sa-z]/gi, '')
      .trim()
      .replaceAll(' ', '_'); // use replaceAll instead of replace(/\s+/g, '_')

    // Ensure ID is unique
    let uniqueId = id;
    let counter = 1;
    while (usedIds.has(uniqueId)) {
      uniqueId = `${id}_${counter++}`;
    }

    tag.setAttribute('id', uniqueId);
    usedIds.add(uniqueId);
  });

  return document.body.innerHTML;
}



// Move helper outside so it's not redefined every render
export function FormattedDateFull(dateString: string | null | undefined) {
  if (!dateString || dateString === '0' || dateString.trim() === '') return "";

  const dateObject = new Date(dateString);
  if (Number.isNaN(dateObject.getTime())) return ""; // handle invalid dates safely

  const day = getOrdinal(dateObject.getDate());
  const month = dateObject.toLocaleString('en-US', { month: 'long' });
  const year = dateObject.getFullYear();

  return `${day} ${month}, ${year}`;

}
/**
 * CF/bootstrap-tabdrop style visibility calculation based on actual DOM wrapping.
 * Mirrors legacy behavior: show dropdown in flow, then move wrapped items (offsetTop > first row).
 */
export const calculateTabdropVisibility = (
  orderedIds: Array<number>,
  itemElements: Map<number, HTMLElement>,
  dropdownElement?: HTMLElement | null
) => {
  const availableIds = orderedIds.filter((id) => itemElements.has(id));
  if (availableIds.length === 0) {
    return { visible: [] as Array<number>, hidden: [] as Array<number> };
  }

  // Show all tabs for measurement pass.
  availableIds.forEach((id) => {
    itemElements.get(id)?.classList.remove('d-none');
  });

  // Keep "More" tab in layout flow while measuring (hidden visually).
  if (dropdownElement) {
    dropdownElement.classList.remove('d-none');
    dropdownElement.style.visibility = 'hidden';
  }

  const firstVisible = availableIds
    .map((id) => itemElements.get(id))
    .find(Boolean);

  if (!firstVisible) {
    if (dropdownElement) {
      dropdownElement.classList.add('d-none');
      dropdownElement.style.visibility = '';
    }
    return { visible: [] as Array<number>, hidden: [] as Array<number> };
  }

  const firstRowTop = firstVisible.offsetTop;
  const hiddenIds = availableIds.filter((id) => {
    const element = itemElements.get(id);
    return Boolean(element && element.offsetTop > firstRowTop);
  });

  // Stabilize like tabdrop: once wrapped tabs are moved, check wrap again.
  if (hiddenIds.length > 0) {
    let changed = true;
    while (changed) {
      changed = false;
      hiddenIds.forEach((id) => {
        itemElements.get(id)?.classList.add('d-none');
      });

      const remainingIds = availableIds.filter((id) => !hiddenIds.includes(id));
      const firstRemaining = remainingIds
        .map((id) => itemElements.get(id))
        .find(Boolean);

      if (!firstRemaining) break;
      const remainingTop = firstRemaining.offsetTop;

      remainingIds.forEach((id) => {
        const element = itemElements.get(id);
        if (element && element.offsetTop > remainingTop && !hiddenIds.includes(id)) {
          hiddenIds.push(id);
          changed = true;
        }
      });
    }
  }

  // Ensure dropdown trigger itself does not wrap to next line.
  // If it wraps, progressively hide trailing visible tabs until it fits first row.
  if (dropdownElement) {
    const hiddenSet = new Set(hiddenIds);
    let remainingIds = availableIds.filter((id) => !hiddenSet.has(id));
    while (remainingIds.length > 0 && dropdownElement.offsetTop > firstRowTop) {
      const lastVisibleId = remainingIds.at(-1);
      if (lastVisibleId == null) {
        break;
      }
      hiddenSet.add(lastVisibleId);
      hiddenIds.push(lastVisibleId);
      itemElements.get(lastVisibleId)?.classList.add('d-none');
      remainingIds = availableIds.filter((id) => !hiddenSet.has(id));
    }
  }

  // Cleanup temporary measurement classes; render state will apply final classes.
  const hiddenSet = new Set(hiddenIds);
  availableIds.forEach((id) => {
    const element = itemElements.get(id);
    if (!element) return;
    if (hiddenSet.has(id)) {
      element.classList.add('d-none');
    } else {
      element.classList.remove('d-none');
    }
  });
  if (dropdownElement) {
    dropdownElement.style.visibility = '';
    if (hiddenIds.length === 0) {
      dropdownElement.classList.add('d-none');
    }
  }

  const visibleIds = availableIds.filter((id) => !hiddenSet.has(id));
  return { visible: visibleIds, hidden: hiddenIds };
};
export const extractIdsFromTreeNodes = (rawIds: Array<string>) => {
  const questionIdRegex = /Q(\d+)/;
  const subjectIdRegex = /S(\d+)/;

  const questionIdsString = rawIds
    ?.filter(Boolean)
    .map((id) => questionIdRegex.exec(id)?.[1] ?? null)
    .filter(Boolean)
    .join(',');

  const subjectIdString = [
    ...new Set(
      rawIds
        ?.filter(Boolean)
        .map((id) => subjectIdRegex.exec(id)?.[1] ?? null)
        .filter(Boolean)
    ),
  ].join(',');

  return { questionIdsString, subjectIdString };
};
export const useGetPageName = () => {
  const location = useLocation();
  const filewithRoute = location.pathname.split("/").pop();
  return (filewithRoute ?? '').split(".")[0];
}
// gridUtils.ts
export function getOpinionGridStyles(
  opinionArray: Array<number> = [],
  questionWidth = 250,
  templateWidth = 224
) {
  let templateMeasurement = "px";
  let aoGridWidth: string | number = (templateWidth * opinionArray.length) + questionWidth;

  if (opinionArray.length <= 6) {
    templateWidth = 1;
    templateMeasurement = "fr";
    aoGridWidth = ""; // match your CF logic
  }

  const gridTemplateColumns = `${questionWidth}px ${opinionArray
    .map(() => `${templateWidth}${templateMeasurement}`)
    .join(" ")}`;

  return {
    display: "grid",
    gridTemplateColumns,
    gridColumnGap: "1px",
    gridRowGap: "5px",
    width: aoGridWidth ? `${aoGridWidth}px` : "auto",
  };
}

// this is function for remove &nbsp; entities from your response so you can get exact text what you want 
export function decodeEntities(string: string): string {
  const parser = new DOMParser();
  const decodedString =
    parser.parseFromString(string, 'text/html').body.textContent ?? string;

  // Remove non-breaking spaces (\u00A0) completely
  return decodedString.replaceAll('\u00A0', ' ');
}
let elm: HTMLInputElement | null = null;

export function isValidURL(u: string) {
  if (!elm) {
    elm = document.createElement('input');
    elm.setAttribute('type', 'url');
  }
  elm.value = u;
  return elm.validity.valid;
}
export const formatDateForSourceDate = (date: string | Date): string => {
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, "0");
  const monthShort = d.toLocaleString("en-GB", { month: "short" });
  const month = monthShort.slice(0, 3);
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
};
export const toIsoNoMs = (input?: string | Date | null): string => {
  if (!input) return '';
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().split('.')[0]; // YYYY-MM-DDTHH:mm:ss
};

// use this function instead of void for ignoring sonar issue
export function safeNavigate(action: () => void | Promise<void>): void;
export function safeNavigate(
  navigateFn: (path: string) => void | Promise<void>,
  path: string
): void;
export function safeNavigate(
  arg1: (() => void | Promise<void>) | ((path: string) => void | Promise<void>),
  path?: string
): void {
  if (typeof path === 'string') {
    Promise.resolve((arg1)(path)).catch((error) => {
      logger.error('Navigation error:', error);
    });
  } else {
    Promise.resolve((arg1 as () => void | Promise<void>)()).catch((error) => {
      logger.error('Action error:', error);
    });
  }
}

export const toCommaSeparatedString = (array: Array<string>) => {
  if (!Array.isArray(array) || array.length === 0) {
    return '';
  }
  return array.map(String).join(',');
};

/**
 * Strip HTML tags from a string using safe character-by-character scanning.
 * Avoids regex-based approaches that could trigger ReDoS concerns (SonarQube S5852).
 * @param html - The HTML string to strip tags from
 * @param replacement - Optional string to replace each tag with (default: '')
 */
export const stripHtmlTags = (html: string, replacement = ''): string => {
  let result = '';
  let inTag = false;
  for (const element of html) {
    if (element === '<') {
      inTag = true;
    } else if (element === '>' && inTag) {
      inTag = false;
      result += replacement;
    } else if (!inTag) {
      result += element;
    }
  }
  return result;
};

export const normalizeHtml = (html: string | undefined): string => {
  if (!html) return '';
  let normalized = html;
  normalized = normalized.replaceAll(/(<br\s*\/?>)\s*<b\s*>\s*<\/b>/gi, '<br />');
  normalized = normalized.replaceAll(/<b\s*>\s*<\/b>/gi, '<br />');
  normalized = normalized.replaceAll(/<br\s*\/?>\s*\r\n/gi, '<br />');
  normalized = normalized.replaceAll(/<br\s*\/?>\s*\n/gi, '<br />');
  normalized = normalized.replaceAll(/<\/br>\s*\r\n/gi, '<br />');
  normalized = normalized.replaceAll(/<\/br>\s*\n/gi, '<br />');
  normalized = normalized.replaceAll(/<\/br>/gi, '<br />');
  // Use non-overlapping whitespace classes to prevent ReDoS (S5852)
  normalized = normalized.replaceAll(/>[^\S\n\r]*[\n\r]+[^\S\n\r]*</g, '><');
  normalized = normalized.replaceAll(/<br\s*>/gi, '<br />');
  normalized = normalized.replaceAll(/<br\s*\/>/gi, '<br />');
  normalized = normalized.replaceAll('\r\n', '<br />');
  normalized = normalized.replaceAll('\n', '<br />');
  // Use [^\S<]* between br tags to avoid nested quantifier overlap (S5852)
  normalized = normalized.replaceAll(/(<br\s*\/?>)(\s*<br\s*\/?>){2,}/gi, '<br /><br />');
  return normalized;
};

export const loadGridResources = async () => {
  const DHTML_GRID_VERSION = '9.1.0';
  await loadCss(`/dhtmlxGrid/codebase_${DHTML_GRID_VERSION}/suite.css`);
  await loadScript(`/dhtmlxGrid/codebase_${DHTML_GRID_VERSION}/suite.js`);

}
export const cfStyleSort = (a: string, b: string): number => {
  const aTrim = a.trim();
  const bTrim = b.trim();
  
  // Handle "n/a" (lowercase) first, as observed
  const aIsLowerNA = aTrim === 'n/a';
  const bIsLowerNA = bTrim === 'n/a';
  if (aIsLowerNA && !bIsLowerNA) return -1;
  if (!aIsLowerNA && bIsLowerNA) return 1;
  if (aIsLowerNA && bIsLowerNA) return 0;
  
  // Uppercase-first before lowercase-first
  const aFirstUpper = a.charAt(0) >= 'A' && a.charAt(0) <= 'Z';
  const bFirstUpper = b.charAt(0) >= 'A' && b.charAt(0) <= 'Z';
  if (aFirstUpper && !bFirstUpper) return -1;
  if (!aFirstUpper && bFirstUpper) return 1;
  
  // Within groups, pure ASCII: compare char codes directly
  for (let index = 0; index < Math.min(a.length, b.length); index++) {
    const codeA = a.codePointAt(index);
    const codeB = b.codePointAt(index);
     if (codeA === undefined || codeB === undefined) continue;
    if (codeA !== codeB) {
      return codeA - codeB;
    }
  }
  // Equal prefix: shorter first
  return a.length - b.length;
};

export const toBase64 = (value: string): string => {
  if (typeof btoa !== 'undefined') {
    return btoa(unescape(encodeURIComponent(value)));
  }
  return Buffer.from(value, 'utf8').toString('base64');
};

export { getChainedDecryptEurl } from './getChainedDecryptEurl';


export const normalizeSelectedTabId = (value: unknown): string => {
  if (value == null) return '';
  if (typeof value !== 'string' && typeof value !== 'number') return '';
  if (typeof value === 'number' && !Number.isFinite(value)) return '';

  const normalizedValue = String(value).trim();
  // "0" means no explicit tab/subject from CFM; let UI select first available.
  return normalizedValue === '0' ? '' : normalizedValue;
};

const BRAND_TITLE = 'aosphere';

const normalizeTitlePart = (value?: string | null): string => {
  if (!value) return '';
  return value.trim().replaceAll(/\s+/g, ' ');
};

export const buildBrowserTitle = ({
  primary,
  secondary,
  brand = BRAND_TITLE,
}: {
  primary?: string | null;
  secondary?: string | null;
  brand?: string;
}): string => {
  const normalizedPrimary = normalizeTitlePart(primary);
  const normalizedSecondary = normalizeTitlePart(secondary);
  const normalizedBrand = normalizeTitlePart(brand) || BRAND_TITLE;

  if (normalizedPrimary && normalizedSecondary) {
    return `${normalizedPrimary}: ${normalizedSecondary} - ${normalizedBrand}`;
  }
  if (normalizedPrimary) {
    return `${normalizedPrimary} - ${normalizedBrand}`;
  }
  return normalizedBrand;
};

const parseTitleText = (text?: string | null): string | null => {
  if (!text) return null;
  
  const parser = new DOMParser();
  const document_ = parser.parseFromString(text, 'text/html');
  const plainText = document_.body.textContent || text;
  
  return plainText.trim();
};

export const setBrowserTitle = (titleParts: {
  primary?: string | null;
  secondary?: string | null;
  brand?: string;
}): void => {
  if (typeof document === 'undefined') return;

  const parsedTitleParts = {
    primary: parseTitleText(titleParts.primary),
    secondary: parseTitleText(titleParts.secondary),
    brand: titleParts.brand,
  };

  const title = buildBrowserTitle(parsedTitleParts);
  document.title = title;
  postParentBrowserTitle(title);
};


const postParentBrowserTitle = (title: string): void => {
  if (typeof window === 'undefined') return;
  if (window.parent === window) return;

  // Referrer becomes the previous V2 page after a full navigation inside the
  // iframe. The parent frame origin does not.
  const ancestorOrigin = globalThis.location.ancestorOrigins?.item(0);
  const targetOrigin =
    ancestorOrigin || process.env.REACT_APP_V3_SHELL_ORIGIN;
  if (!targetOrigin) return;

  window.parent.postMessage(
    { type: 'BROWSER_TITLE', title },
    targetOrigin
  );
};